import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';

import { UserAccountService } from '../../../services/user-account/user-account.service';
import { CreditsLabelPipe } from '../../../pipes/credits-label.pipe';

@Component({
    selector: 'app-sidebar',
    standalone: true,
    imports: [CommonModule, RouterModule, CreditsLabelPipe],
    templateUrl: './sidebar.component.html',
    styleUrl: './sidebar.component.css',
})
export class SidebarComponent implements OnInit, AfterViewInit, OnDestroy {

    subscription: any = {};
    availableCredits: number | null = null;
    creditsLoading = true;
    /** Read stored state before first paint to avoid open/close flicker on route changes. */
    isCollapsed = SidebarComponent.readStoredCollapsed();
    /** Width transition only after mount — remounts should not animate. */
    allowWidthTransition = false;
    /** Fixed-position label for collapsed nav items (avoids overflow clipping). */
    floatingTooltip: { visible: boolean; label: string; top: number; left: number } = {
        visible: false,
        label: '',
        top: 0,
        left: 0,
    };
    private readonly sidebarStateKey = 'SIDEBAR_COLLAPSED';
    private creditsSubscription?: Subscription;
    private hoveredNavLink: HTMLElement | null = null;

    private onMobileSidebarToggleBound = () => this.toggleSidebar();
    private readonly onMobileNavPointer = (event: Event) => this.onMobileNavClick(event);
    private navTooltipListenersAttached = false;
    private lastWasMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

    constructor(
        public router: Router,
        private userAccountService: UserAccountService,
        private host: ElementRef<HTMLElement>,
    ) { }

    ngOnInit(): void {
        this.retrieveSubscriptionDetails();
        this.loadAvailableCredits();
        // Desktop "expanded" must not open the mobile drawer and cover page content.
        if (window.innerWidth <= 768) {
            this.isCollapsed = true;
            document.body.classList.remove('mobile-sidebar-open');
        } else {
            this.syncMobileSidebarBodyClass();
        }
        window.addEventListener('mobile-sidebar-toggle', this.onMobileSidebarToggleBound);
        window.addEventListener('resize', this.onViewportResizeBound);
    }

    ngAfterViewInit(): void {
        const root = this.host.nativeElement;
        root.addEventListener('click', this.onMobileNavPointer, true);
        this.attachNavTooltipListeners();
        // Enable transitions only after the first frame so navigation remounts stay still.
        requestAnimationFrame(() => {
            this.allowWidthTransition = true;
        });
    }

    ngOnDestroy(): void {
        this.creditsSubscription?.unsubscribe();
        window.removeEventListener('mobile-sidebar-toggle', this.onMobileSidebarToggleBound);
        window.removeEventListener('resize', this.onViewportResizeBound);
        this.host.nativeElement.removeEventListener('click', this.onMobileNavPointer, true);
        this.detachNavTooltipListeners();
        this.hideFloatingTooltip();
        // Defer cleanup so navigating between pages (new sidebar mounts) doesn't flash closed.
        queueMicrotask(() => {
            if (!document.querySelector('app-sidebar')) {
                document.body.classList.remove('mobile-sidebar-open');
            }
        });
    }

    private readonly onViewportResizeBound = () => {
        const isMobile = window.innerWidth <= 768;
        if (isMobile === this.lastWasMobile) {
            return;
        }
        this.lastWasMobile = isMobile;
        if (isMobile) {
            this.isCollapsed = true;
            document.body.classList.remove('mobile-sidebar-open');
            return;
        }
        document.body.classList.remove('mobile-sidebar-open');
        this.isCollapsed = SidebarComponent.readStoredCollapsed();
    };

    onBuyCreditsClick(): void {
        this.closeMobileSidebar();
        this.router.navigate(['/plan-pricing']);
    }

    onMobileNavClick(event: Event): void {
        const target = event.target as HTMLElement | null;
        if (!target?.closest('a, button.sidebar-buy-credits-btn')) {
            return;
        }
        this.closeMobileSidebar();
    }

    private closeMobileSidebar(): void {
        const menuIsOpen = document.body.classList.contains('mobile-sidebar-open');
        if (window.innerWidth > 768 && !menuIsOpen) {
            return;
        }
        this.isCollapsed = true;
        localStorage.setItem(this.sidebarStateKey, 'true');
        this.hideFloatingTooltip();
        document.body.classList.remove('mobile-sidebar-open');
    }

    private attachNavTooltipListeners(): void {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
            return;
        }
        const nav = this.host.nativeElement.querySelector('.sidebar-nav');
        if (!nav) {
            return;
        }
        nav.addEventListener('mouseover', this.onNavTooltipShowBound);
        nav.addEventListener('mouseout', this.onNavTooltipHideBound);
        nav.addEventListener('focusin', this.onNavTooltipShowBound);
        nav.addEventListener('focusout', this.onNavTooltipHideBound);
        this.navTooltipListenersAttached = true;
    }

    private detachNavTooltipListeners(): void {
        if (!this.navTooltipListenersAttached) {
            return;
        }
        const nav = this.host.nativeElement.querySelector('.sidebar-nav');
        nav?.removeEventListener('mouseover', this.onNavTooltipShowBound);
        nav?.removeEventListener('mouseout', this.onNavTooltipHideBound);
        nav?.removeEventListener('focusin', this.onNavTooltipShowBound);
        nav?.removeEventListener('focusout', this.onNavTooltipHideBound);
        this.navTooltipListenersAttached = false;
    }

    private readonly onNavTooltipShowBound = (event: Event) => this.onNavTooltipShow(event);
    private readonly onNavTooltipHideBound = (event: Event) => this.onNavTooltipHide(event);

    toggleSidebar(): void {
        this.isCollapsed = !this.isCollapsed;
        localStorage.setItem(this.sidebarStateKey, String(this.isCollapsed));
        this.hideFloatingTooltip();
        this.syncMobileSidebarBodyClass();
    }

    onNavTooltipShow(event: Event): void {
        if (!this.isCollapsed) {
            return;
        }
        const link = (event.target as HTMLElement | null)?.closest?.('a[data-label]') as HTMLElement | null;
        if (!link) {
            return;
        }
        this.hoveredNavLink = link;
        this.positionFloatingTooltip(link);
    }

    onNavTooltipHide(event: Event): void {
        const related = (event as MouseEvent).relatedTarget as Node | null;
        const link = (event.target as HTMLElement | null)?.closest?.('a[data-label]') as HTMLElement | null;
        if (link && related && link.contains(related)) {
            return;
        }
        if (this.hoveredNavLink && related && this.hoveredNavLink.contains(related)) {
            return;
        }
        this.hideFloatingTooltip();
    }

    onSidebarNavScroll(): void {
        if (!this.isCollapsed || !this.hoveredNavLink) {
            return;
        }
        this.positionFloatingTooltip(this.hoveredNavLink);
    }

    private positionFloatingTooltip(link: HTMLElement): void {
        const label = link.getAttribute('data-label')?.trim();
        if (!label) {
            this.hideFloatingTooltip();
            return;
        }
        const rect = link.getBoundingClientRect();
        this.floatingTooltip = {
            visible: true,
            label,
            top: rect.top + rect.height / 2,
            left: rect.right + 10,
        };
    }

    private hideFloatingTooltip(): void {
        this.hoveredNavLink = null;
        if (!this.floatingTooltip.visible) {
            return;
        }
        this.floatingTooltip = { ...this.floatingTooltip, visible: false };
    }

    private static readStoredCollapsed(): boolean {
        try {
            const storedState = localStorage.getItem('SIDEBAR_COLLAPSED');
            if (storedState === null) {
                return true;
            }
            return storedState === 'true';
        } catch {
            return true;
        }
    }

    private syncMobileSidebarBodyClass(): void {
        if (window.innerWidth > 768) return;
        document.body.classList.toggle('mobile-sidebar-open', !this.isCollapsed);
    }

    retrieveSubscriptionDetails(): void {
        const USER_PORTAL = localStorage.getItem('USER_PORTAL');
        if (USER_PORTAL) {
            const userPortal = JSON.parse(USER_PORTAL);
            this.subscription = userPortal?.subscriptionDetails || {};
            const storedCredits =
                this.subscription?.available_credits ?? this.subscription?.remaining_credits;
            if (storedCredits != null && this.availableCredits == null) {
                this.availableCredits = storedCredits;
            }
        }
    }

    private loadAvailableCredits(): void {
        this.creditsLoading = true;
        this.creditsSubscription = this.userAccountService.getUserProfile().subscribe({
            next: (response) => {
                const subscription = response?.data?.subscriptions?.[0] || {};
                this.availableCredits = this.userAccountService.pickAvailableCredits(response);
                this.creditsLoading = false;
            },
            error: () => {
                if (this.availableCredits == null) {
                    this.availableCredits = 0;
                }
                this.creditsLoading = false;
            },
        });
    }

    isActive(keyword: string): boolean {
        return this.router.url.includes(keyword);
    }

    isHomeActive(): boolean {
        return this.router.url.split('?')[0] === '/home';
    }

    isOsintSearchActive(): boolean {
        const path = this.router.url.split('?')[0];
        return path.includes('osint') && !path.includes('osint-image-search');
    }

    goToHome(event?: Event): void {
        event?.preventDefault();
        this.closeMobileSidebar();
        void this.router.navigateByUrl('/home');
    }

    goToOsintSearch(event?: Event): void {
        event?.preventDefault();
        this.closeMobileSidebar();
        void this.router.navigateByUrl('/osint-number-search');
    }
}
